import type { MouseEvent} from 'react';
import { useCallback, useContext, useState } from 'react';
import gtag from '../../analytics/gtag';
import * as api from '../../api';
import { productAPI } from '../../config';
import ErrorBox from '../../generic/ErrorBox';
import Icon from '../../generic/Icon';
import ScrollableDiv from '../../generic/ScrollableDiv';
import { TrackingContext, WrapTrackingContext } from '../../generic/TrackingContext';
import { getProductDetailsForSR } from '../../model/Product';
import delay from '../../utils/delay';
import { useAsyncAction } from '../../utils/useAsyncAction';
import { useAsyncBlockingEventHandler } from '../../utils/useAsyncBlockingCallback';
import type { ProductCardDetails } from '../ProductCard';
import ProductImage from '../ProductImage';
import * as style from './SimilarProducts.module.css';

interface Props {
    productId: string;
    show: boolean;
}

export default function SimilarProducts({ productId, show }: Props) {
    const [products, setProducts] = useState<ProductCardDetails[]>();
    const [message, setMessage] = useState<string>();

    const [fetch, isLoading, error] = useAsyncAction(async () => {
        const { message, products } = await api.get<{ message: string; products: ProductCardDetails[] }>(productAPI.similarProductsUrl(productId));
        setMessage(message);
        setProducts(products);

        gtag('event', 'view_item_list', {
            items: products.map((p, i) => ({
                item_id: p.code,
                index: i + 1,
            })),
            item_list_name: 'Produkter som ligner',
            item_list_id: productId,
            list_length: products.length,
            list_sort: '',
        });
    });

    const toggle = useAsyncBlockingEventHandler(
        async (e: MouseEvent<HTMLElement>) => {
            if (products) {
                // delay until the transition has ended
                await delay(1_000);
                setProducts(undefined);
            } else {
                e.preventDefault();
                const target = e.currentTarget.closest('details');
                await fetch();
                target!.open = true;
            }
        },
        [products],
    );

    return (
        show && (
            <details className={style.similarProducts}>
                <summary onClick={toggle} className={style.toggleButton}>
                    Produkter som ligner
                    <Icon className={isLoading ? 'spinner-bg' : 'icon-details'} />
                </summary>
                <WrapTrackingContext
                    set={{
                        item_list_name: 'Produkter som ligner',
                        item_list_id: productId,
                    }}
                >
                    <ErrorBox errors={error} />
                    <p className={style.message}>{message}</p>
                    <ScrollableDiv className={style.wrapper}>
                        <ul className={style.similarProductsList}>
                            {products?.map((p, i) => (
                                <SimilarProduct key={p.code} product={p} index={i + 1} />
                            ))}
                        </ul>
                    </ScrollableDiv>
                </WrapTrackingContext>
            </details>
        )
    );
}

function SimilarProduct({ product, index }: { product: ProductCardDetails; index: number }) {
    const { item_list_id, item_list_name } = useContext(TrackingContext);

    const handleInteraction = useCallback(() => {
        gtag('event', 'select_item', {
            items: [{ item_id: product.code, index }],
            item_list_id,
            item_list_name,
        });
    }, [product.code, index, item_list_id, item_list_name]);
    return (
        <li>
            <a href={product.url} className={style.similarProduct} aria-label={getProductDetailsForSR(product)} onClick={handleInteraction}>
                <ProductImage product={product} />
                <span className={style.name}>{product.name}</span>
                <span className={style.price}>{product.price.formattedValue}</span>
            </a>
        </li>
    );
}
